/**
 * Input validation utilities for the Immobilien-Analyzer
 */

export interface ValidationRule {
  min?: number;
  max?: number;
  required?: boolean;
  pattern?: RegExp;
  custom?: (value: any) => boolean;
  message?: string;
}

export interface ValidationRules {
  [key: string]: ValidationRule;
}

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

/**
 * Property input validation rules
 */
export const propertyValidationRules: ValidationRules = {
  // Property details
  propertyName: {
    required: true,
    message: 'Immobilienname ist erforderlich'
  },
  wohnflaeche: {
    min: 10,
    max: 1000,
    required: true,
    message: 'Wohnfläche muss zwischen 10 und 1000 m² liegen'
  },
  zimmer: {
    min: 0.5,
    max: 20,
    message: 'Zimmerzahl muss zwischen 0.5 und 20 liegen'
  },
  baujahr: {
    min: 1800,
    max: new Date().getFullYear() + 5,
    message: `Baujahr muss zwischen 1800 und ${new Date().getFullYear() + 5} liegen`
  },
  
  // Prices
  inseratpreis: {
    min: 1000,
    max: 100000000,
    required: true,
    message: 'Inseratpreis muss zwischen 1.000 € und 100.000.000 € liegen'
  },
  angebotspreis: {
    min: 1000,
    max: 100000000,
    required: true,
    message: 'Angebotspreis muss zwischen 1.000 € und 100.000.000 € liegen'
  },
  
  // Financing
  finanzierungsanteil: {
    min: 0,
    max: 120,
    message: 'Finanzierungsanteil muss zwischen 0% und 120% liegen'
  },
  zinssatz: {
    min: 0,
    max: 20,
    message: 'Zinssatz muss zwischen 0% und 20% liegen'
  },
  zinssatzJ10: {
    min: 0,
    max: 20,
    message: 'Zinssatz Jahr 10+ muss zwischen 0% und 20% liegen'
  },
  tilgung: {
    min: 0,
    max: 100,
    message: 'Tilgung muss zwischen 0% und 100% liegen'
  },
  
  // Tax and fees
  steuersatz: {
    min: 0,
    max: 50,
    message: 'Steuersatz muss zwischen 0% und 50% liegen'
  },
  makler: {
    min: 0,
    max: 10,
    message: 'Maklerprovision muss zwischen 0% und 10% liegen'
  },
  notar: {
    min: 0,
    max: 5,
    message: 'Notargebühren müssen zwischen 0% und 5% liegen'
  },
  grundbuchamt: {
    min: 0,
    max: 2,
    message: 'Grundbuchamt muss zwischen 0% und 2% liegen'
  },
  grunderwerbst: {
    min: 0,
    max: 10,
    message: 'Grunderwerbsteuer muss zwischen 0% und 10% liegen'
  },
  
  // Rent
  kaltmieteProQm: {
    min: 0,
    max: 100,
    message: 'Kaltmiete muss zwischen 0 € und 100 €/m² liegen'
  },
  istKaltmieteProQm: {
    min: 0,
    max: 100,
    message: 'IST Kaltmiete muss zwischen 0 € und 100 €/m² liegen'
  },
  stellplatzMiete: {
    min: 0,
    max: 500,
    message: 'Stellplatzmiete muss zwischen 0 € und 500 € liegen'
  },
  istStellplatzMiete: {
    min: 0,
    max: 500,
    message: 'IST Stellplatzmiete muss zwischen 0 € und 500 € liegen'
  },
  
  // Running costs
  hausgeldUmlagefaehig: {
    min: 0,
    max: 1000,
    message: 'Umlagefähiges Hausgeld muss zwischen 0 € und 1000 € liegen'
  },
  hausgeldNichtUmlagefaehig: {
    min: 0,
    max: 1000,
    message: 'Nicht umlagefähiges Hausgeld muss zwischen 0 € und 1000 € liegen'
  },
  instandhaltungsruecklage: {
    min: 0,
    max: 500,
    message: 'Instandhaltungsrücklage muss zwischen 0 € und 500 € liegen'
  },
  verwaltungskosten: {
    min: 0,
    max: 500,
    message: 'Verwaltungskosten müssen zwischen 0 € und 500 € liegen'
  },
  
  // Percentages
  gebaudeanteil: {
    min: 0,
    max: 100,
    message: 'Gebäudeanteil muss zwischen 0% und 100% liegen'
  },
  afaSatz: {
    min: 0,
    max: 10,
    message: 'AfA-Satz muss zwischen 0% und 10% liegen'
  },
  mietentwicklungProJahr: {
    min: -10,
    max: 10,
    message: 'Mietentwicklung muss zwischen -10% und 10% pro Jahr liegen'
  },
  inflationProJahr: {
    min: -5,
    max: 15,
    message: 'Inflation muss zwischen -5% und 15% pro Jahr liegen'
  },
  wertzuwachsProJahr: {
    min: -10,
    max: 20,
    message: 'Wertzuwachs muss zwischen -10% und 20% pro Jahr liegen'
  },
  mietausfallrisiko: {
    min: 0,
    max: 50,
    message: 'Mietausfallrisiko muss zwischen 0% und 50% liegen'
  },
  
  // Maintenance
  instandhaltung: {
    min: 0,
    max: 100,
    message: 'Instandhaltung muss zwischen 0 € und 100 €/m² liegen'
  },
  instandhaltungJ10: {
    min: 0,
    max: 100,
    message: 'Instandhaltung Jahr 10+ muss zwischen 0 € und 100 €/m² liegen'
  },
  
  // Special investments
  anfangsinvestitionen: {
    min: 0,
    max: 1000000,
    message: 'Anfangsinvestitionen müssen zwischen 0 € und 1.000.000 € liegen'
  },
  sonderinvestitionJ10: {
    min: 0,
    max: 1000000,
    message: 'Sonderinvestition Jahr 10 muss zwischen 0 € und 1.000.000 € liegen'
  }
};

/**
 * Validate a single value against a rule
 */
export const validateValue = (value: any, rule: ValidationRule): string | null => {
  // Check required
  if (rule.required && (value === undefined || value === null || value === '')) {
    return rule.message || 'Dieses Feld ist erforderlich';
  }
  
  // Skip other validations if value is empty and not required
  if (value === undefined || value === null || value === '') {
    return null;
  }
  
  // Check numeric values
  if (typeof value === 'number') {
    if (rule.min !== undefined && value < rule.min) {
      return rule.message || `Wert muss mindestens ${rule.min} sein`;
    }
    if (rule.max !== undefined && value > rule.max) {
      return rule.message || `Wert darf maximal ${rule.max} sein`;
    }
  }
  
  // Check string patterns
  if (typeof value === 'string' && rule.pattern) {
    if (!rule.pattern.test(value)) {
      return rule.message || 'Ungültiges Format';
    }
  }
  
  // Custom validation
  if (rule.custom && !rule.custom(value)) {
    return rule.message || 'Ungültiger Wert';
  }
  
  return null;
};

/**
 * Validate all property inputs
 */
export const validatePropertyInputs = (inputs: any): ValidationResult => {
  const errors: ValidationError[] = [];
  
  Object.keys(propertyValidationRules).forEach(field => {
    const rule = propertyValidationRules[field];
    const value = inputs[field];
    const error = validateValue(value, rule);
    
    if (error) {
      errors.push({ field, message: error });
    }
  });
  
  // Additional cross-field validations
  if (inputs.angebotspreis && inputs.inseratpreis) {
    if (inputs.angebotspreis > inputs.inseratpreis * 1.5) {
      errors.push({
        field: 'angebotspreis',
        message: 'Angebotspreis sollte nicht mehr als 150% des Inseratpreises betragen'
      });
    }
  }
  
  if (inputs.zinssatzJ10 && inputs.zinssatz) {
    if (Math.abs(inputs.zinssatzJ10 - inputs.zinssatz) > 5) {
      errors.push({
        field: 'zinssatzJ10',
        message: 'Zinssatz Jahr 10+ sollte nicht mehr als 5% vom aktuellen Zinssatz abweichen'
      });
    }
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Sanitize and clamp input value
 */
export const sanitizeInput = (value: any, field: string): any => {
  const rule = propertyValidationRules[field];
  if (!rule) return value;
  
  // Convert to number if needed
  if (typeof value === 'string' && (rule.min !== undefined || rule.max !== undefined)) {
    value = parseFloat(value);
    if (isNaN(value)) return rule.min || 0;
  }
  
  // Clamp numeric values
  if (typeof value === 'number') {
    if (rule.min !== undefined && value < rule.min) {
      return rule.min;
    }
    if (rule.max !== undefined && value > rule.max) {
      return rule.max;
    }
  }
  
  return value;
};

/**
 * Get validation status color
 */
export const getValidationColor = (field: string, value: any): string => {
  const rule = propertyValidationRules[field];
  if (!rule) return '';
  
  const error = validateValue(value, rule);
  if (error) return 'border-red-500';
  
  if (rule.required && value) return 'border-green-500';
  return '';
};

/**
 * Format validation error messages for display
 */
export const formatValidationErrors = (errors: ValidationError[]): string => {
  if (errors.length === 0) return '';
  
  return errors.map(e => `${e.field}: ${e.message}`).join('\n');
};

/**
 * Validate CSV import data
 */
export const validateCSVData = (data: any[]): ValidationResult => {
  const errors: ValidationError[] = [];
  
  if (!Array.isArray(data)) {
    return {
      valid: false,
      errors: [{ field: 'data', message: 'Ungültige Datenstruktur' }]
    };
  }
  
  data.forEach((row, index) => {
    const rowErrors = validatePropertyInputs(row);
    rowErrors.errors.forEach(error => {
      errors.push({
        field: `Zeile ${index + 1}: ${error.field}`,
        message: error.message
      });
    });
  });
  
  return {
    valid: errors.length === 0,
    errors
  };
};